/**
 * Composable for generating responsive image attributes
 * Provides srcset, sizes, alt text, and dimensions for better performance
 *
 * Based on performance optimization playbook - Consort Homes implementation
 * Achieves: 800% mobile performance improvement, -70% TBT, perfect CLS
 */
export const useResponsiveImage = (image, preferredSize = null, customSizes = null) => {
  if (!image) {
    return {
      src: '',
      alt: '',
      loading: 'lazy',
      decoding: 'async'
    };
  }

  // Special case: 'full' size returns only the original image without srcset
  if (preferredSize === 'full') {
    return {
      src: image.sourceUrl || '',
      alt: image.altText || image.title || '',
      width: image.mediaDetails?.width,
      height: image.mediaDetails?.height,
      loading: 'lazy',
      decoding: 'async'
    };
  }

  const srcset = [];
  let targetAspectRatio = null;
  let preferredSizeData = null;
  // Allow 20% tolerance for aspect ratio matching against a preferred size.
  // This excludes ultra-wide images (2.18:1) while including reasonable variations (1.15-1.35:1)
  let tolerance = 0.20;

  // No preferred size means "show the whole image": only use sizes WordPress
  // scaled proportionally (large, medium_large, 1536x1536...). Otherwise the
  // browser can pick a hard-cropped size like full-brand-window or medium-square.
  // The small tolerance only absorbs WordPress's pixel rounding.
  if (!preferredSize && image.mediaDetails?.width && image.mediaDetails?.height) {
    targetAspectRatio = image.mediaDetails.width / image.mediaDetails.height;
    tolerance = 0.02;
  }

  // If a preferred size is specified, find it and calculate its aspect ratio
  if (preferredSize && image.mediaDetails?.sizes && Array.isArray(image.mediaDetails.sizes)) {
    preferredSizeData = image.mediaDetails.sizes.find(size => size.name === preferredSize);

    if (preferredSizeData && preferredSizeData.width && preferredSizeData.height) {
      targetAspectRatio = preferredSizeData.width / preferredSizeData.height;
    }
  }

  // Use WordPress's actual generated image sizes from mediaDetails
  if (image.mediaDetails?.sizes && Array.isArray(image.mediaDetails.sizes)) {
    // Filter out gform-specific sizes and sort by width
    let availableSizes = image.mediaDetails.sizes
      .filter(size => !size.name?.startsWith('gform-'))
      .sort((a, b) => a.width - b.width);

    // If we have a target aspect ratio, filter to only include similar aspect ratios
    // This prevents aspect ratio mismatches that cause cropping issues
    if (targetAspectRatio !== null) {
      availableSizes = availableSizes.filter(size => {
        if (!size.width || !size.height) return false;
        const sizeAspectRatio = size.width / size.height;
        return Math.abs(sizeAspectRatio - targetAspectRatio) / targetAspectRatio <= tolerance;
      });
    }

    // Build srcset from available sizes
    availableSizes.forEach(size => {
      if (size.sourceUrl && size.width) {
        srcset.push(`${size.sourceUrl} ${size.width}w`);
      }
    });
  }

  // Include the original image as fallback only if no preferred size specified
  // or if its aspect ratio matches (within tolerance)
  if (image.sourceUrl && image.mediaDetails?.width && image.mediaDetails?.height) {
    const shouldIncludeOriginal = !targetAspectRatio || (() => {
      const originalAspectRatio = image.mediaDetails.width / image.mediaDetails.height;
      return Math.abs(originalAspectRatio - targetAspectRatio) / targetAspectRatio <= tolerance;
    })();

    if (shouldIncludeOriginal) {
      srcset.push(`${image.sourceUrl} ${image.mediaDetails.width}w`);
    }
  }

  // Use the preferred size as the default src, fallback to original
  const defaultSrc = preferredSizeData?.sourceUrl || image.sourceUrl || '';

  return {
    src: defaultSrc,
    srcset: srcset.length > 0 ? srcset.join(', ') : undefined,
    // Responsive sizes - adjust based on your layout
    // Can be overridden with customSizes parameter for specific components
    sizes: customSizes || '(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw',
    alt: image.altText || image.title || '',
    width: preferredSizeData?.width || image.mediaDetails?.width,
    height: preferredSizeData?.height || image.mediaDetails?.height,
    loading: 'lazy',
    decoding: 'async'
  };
};

/**
 * Generate srcset for background images converted to <img>
 * Used for hero sections and other components that previously used CSS background-image
 */
export const useBackgroundImage = (image) => {
  const attrs = useResponsiveImage(image);

  return {
    ...attrs,
    class: 'absolute inset-0 w-full h-full object-cover -z-10',
    alt: '', // Background images should have empty alt
  };
};
